import type React from 'react';
import { useState, useEffect, useId, useSyncExternalStore } from 'react';
import type { CrystalGuide } from './crystalGlobeData';
import { AURAS } from './crystalGlobeData';

// ── Dark-mode detection ────────────────────────────────────────────────

function subscribeDarkMode(cb: () => void) {
  const obs = new MutationObserver(cb);
  obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  return () => obs.disconnect();
}
function getDarkMode() {
  return document.documentElement.classList.contains('dark-mode');
}
function useDarkMode() {
  return useSyncExternalStore(subscribeDarkMode, getDarkMode, () => true);
}

/** Return the bg pair that matches the current theme. */
function themeBg(guide: CrystalGuide, dark: boolean): [string, string] {
  return dark ? guide.bg : guide.bgLight;
}

// ── Types ───────────────────────────────────────────────────────────────

export interface CrystalGlobeProps {
  guide: CrystalGuide;
  auraId: string;
  equippedItems?: string[];
  size?: number;
  reducedMotion?: boolean;
}

/** Compact mode — renders just the crystal body in a circle, no globe. */
export interface CrystalBodyOnlyProps {
  guide: CrystalGuide;
  auraId?: string;
  size?: number;
  /** Render the raw crystal with no background circle or clipping. */
  freeform?: boolean;
  /** Zoom into the face/head area for small bubble avatars. */
  faceCloseUp?: boolean;
}

interface EyeState {
  h: number;
  dy: number;
  hi: number;
  sz: number;
  closed?: boolean;
}

// ── Eye states per aura ─────────────────────────────────────────────────

const EYE_STATES: Record<string, EyeState> = {
  none:       { h: 1,    dy: 0,   hi: 0.6,  sz: 1    },
  joy:        { h: 0.75, dy: 0.3, hi: 0.8,  sz: 1.05 },
  pain:       { h: 0.6,  dy: 0.5, hi: 0.3,  sz: 0.95 },
  grief:      { h: 0.7,  dy: 0.8, hi: 0.25, sz: 0.95 },
  rest:       { h: 0.08, dy: 0.5, hi: 0,    sz: 1, closed: true },
  breathing:  { h: 1.05, dy: 0,   hi: 0.55, sz: 1.05 },
  healing:    { h: 0.9,  dy: 0,   hi: 0.65, sz: 1    },
  creative:   { h: 0.95, dy: 0,   hi: 0.7,  sz: 1    },
  protection: { h: 0.8,  dy: 0.2, hi: 0.5,  sz: 0.95 },
  resistance: { h: 0.85, dy: 0,   hi: 0.55, sz: 1    },
  community:  { h: 0.95, dy: 0,   hi: 0.75, sz: 1    },
  spiritual:  { h: 0.9,  dy: 0,   hi: 0.5,  sz: 1    },
  careweave:  { h: 0.95, dy: 0,   hi: 0.65, sz: 1    },
};

// ── Helpers ─────────────────────────────────────────────────────────────

function useAnimPhase(reducedMotion?: boolean) {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    if (reducedMotion) return;
    const id = setInterval(() => setPhase(p => (p + 0.02) % (Math.PI * 200)), 40);
    return () => clearInterval(id);
  }, [reducedMotion]);
  return phase;
}

// ── Crystal Body ────────────────────────────────────────────────────────

function CrystalBody({
  guide, auraId, phase, cx, cy, su, breathe,
}: {
  guide: CrystalGuide; auraId: string; phase: number;
  cx: number; cy: number; su: number; breathe: number;
}) {
  const sin = Math.sin;
  const baseBlink = sin(phase * 2.5) > 0.96 ? 0.15 : 1;
  const es = EYE_STATES[auraId] || EYE_STATES.none;
  const blink = baseBlink < 0.5 ? baseBlink : es.h;
  const eyeDy = es.dy;
  const eyeHi = es.hi;
  const eyeSz = es.sz;
  const isJade = guide.shape === 'pair';

  const shapes: Record<string, React.ReactElement> = {
    chunky: <g>
      <ellipse cx={cx} cy={cy+su*3} rx={su*20} ry={su*22} fill={guide.body}/>
      <ellipse cx={cx} cy={cy+su*14} rx={su*22} ry={su*12} fill={guide.body}/>
      <ellipse cx={cx} cy={cy+su*5} rx={su*13} ry={su*16} fill={guide.belly} opacity="0.3"/>
      <ellipse cx={cx} cy={cy-su*22} rx={su*4} ry={su*6} fill={guide.accent} opacity="0.5"/>
    </g>,
    round: <g>
      <circle cx={cx} cy={cy} r={su*22} fill={guide.body}/>
      <circle cx={cx} cy={cy+su*2} r={su*14} fill={guide.belly} opacity="0.35"/>
      <circle cx={cx-su*18} cy={cy-su*13} r={su*5} fill={guide.body}/>
      <circle cx={cx+su*18} cy={cy-su*13} r={su*5} fill={guide.body}/>
    </g>,
    egg: <g>
      <ellipse cx={cx} cy={cy+su*2} rx={su*19} ry={su*22} fill={guide.body}/>
      <ellipse cx={cx} cy={cy+su*4} rx={su*12} ry={su*16} fill={guide.belly} opacity="0.3"/>
      <ellipse cx={cx} cy={cy-su*17} rx={su*11} ry={su*5} fill={guide.belly} opacity="0.6"/>
      {([-5,-2,2,5] as number[]).map((dx,i) => <ellipse key={i} cx={cx+su*dx} cy={cy-su*20} rx={su*1.5} ry={su*3} fill={guide.cheek} opacity="0.5"/>)}
    </g>,
    teardrop: <g>
      <path d={`M${cx},${cy-su*22} Q${cx+su*10},${cy-su*17} ${cx+su*18},${cy-su*3} Q${cx+su*19},${cy+su*8} ${cx+su*16},${cy+su*17} Q${cx+su*8},${cy+su*24} ${cx},${cy+su*24} Q${cx-su*8},${cy+su*24} ${cx-su*16},${cy+su*17} Q${cx-su*19},${cy+su*8} ${cx-su*18},${cy-su*3} Q${cx-su*10},${cy-su*17} ${cx},${cy-su*22}Z`} fill={guide.body}/>
      <ellipse cx={cx} cy={cy+su*3} rx={su*11} ry={su*13} fill={guide.belly} opacity="0.35"/>
    </g>,
    tall: <g>
      <rect x={cx-su*15} y={cy-su*22} width={su*30} height={su*44} rx={su*11} fill={guide.body}/>
      <rect x={cx-su*10} y={cy-su*14} width={su*20} height={su*30} rx={su*8} fill={guide.belly} opacity="0.25"/>
    </g>,
    pair: <g>
      <ellipse cx={cx-su*6} cy={cy+su*1} rx={su*15} ry={su*19} fill={guide.body}/>
      <ellipse cx={cx+su*10} cy={cy+su*3} rx={su*11} ry={su*16} fill={guide.belly} opacity="0.85"/>
    </g>,
    shard: <g>
      <path d={`M${cx-su*3},${cy-su*24} Q${cx+su*16},${cy-su*14} ${cx+su*14},${cy+su*6} Q${cx+su*11},${cy+su*22} ${cx-su*2},${cy+su*24} Q${cx-su*14},${cy+su*20} ${cx-su*16},${cy+su*5} Q${cx-su*18},${cy-su*10} ${cx-su*3},${cy-su*24}Z`} fill={guide.body}/>
    </g>,
    sphere: <g>
      <circle cx={cx} cy={cy} r={su*20} fill={guide.body}/>
      <circle cx={cx} cy={cy} r={su*16} fill={guide.belly} opacity="0.35"/>
      <circle cx={cx} cy={cy} r={su*13} fill={guide.cheek} opacity="0.2"/>
    </g>,
    oblong: <g>
      <ellipse cx={cx} cy={cy} rx={su*16} ry={su*22} fill={guide.body}/>
      <ellipse cx={cx} cy={cy+su*1} rx={su*11} ry={su*15} fill={guide.belly} opacity="0.25"/>
    </g>,
    crescent: <g>
      <path d={`M${cx+su*6},${cy-su*21} Q${cx-su*19},${cy-su*11} ${cx-su*17},${cy+su*3} Q${cx-su*14},${cy+su*19} ${cx},${cy+su*21} Q${cx+su*13},${cy+su*19} ${cx+su*17},${cy+su*6} Q${cx+su*19},${cy-su*8} ${cx+su*6},${cy-su*21}Z`} fill={guide.body}/>
      <circle cx={cx+su*8} cy={cy-su*2} r={su*12} fill={guide.bg[1]} opacity="0.2"/>
    </g>,
  };

  // Mouth renderer
  const renderMouth = () => {
    const e = guide.eye;
    const o = 0.3;
    const w = su * 3;
    const my = cy + su * 3.5;

    if (isJade) {
      const jw = su * 2;
      const jy1 = cy + su * 3;
      const jy2 = cy + su * 4;
      const j1 = cx - su * 9;
      const j2 = cx + su * 9;
      if (auraId === 'joy') return <g>
        <path d={`M${j1-jw},${jy1} Q${j1},${jy1+su*2.5} ${j1+jw},${jy1}`} stroke={e} strokeWidth="0.8" fill="none" strokeLinecap="round" opacity={o+0.1}/>
        <path d={`M${j2-jw},${jy2} Q${j2},${jy2+su*2.5} ${j2+jw},${jy2}`} stroke={e} strokeWidth="0.8" fill="none" strokeLinecap="round" opacity={o+0.1}/>
      </g>;
      if (auraId === 'pain' || auraId === 'grief') return <g>
        <line x1={j1-jw*0.6} y1={jy1} x2={j1+jw*0.6} y2={jy1} stroke={e} strokeWidth="0.8" strokeLinecap="round" opacity={o}/>
        <line x1={j2-jw*0.6} y1={jy2} x2={j2+jw*0.6} y2={jy2} stroke={e} strokeWidth="0.8" strokeLinecap="round" opacity={o}/>
      </g>;
      if (auraId === 'breathing') return <g>
        <ellipse cx={j1} cy={jy1+su*0.3} rx={su*0.8} ry={su*1} fill="none" stroke={e} strokeWidth="0.7" opacity={o}/>
        <ellipse cx={j2} cy={jy2+su*0.3} rx={su*0.8} ry={su*1} fill="none" stroke={e} strokeWidth="0.7" opacity={o}/>
      </g>;
      return <g>
        <path d={`M${j1-jw},${jy1} Q${j1},${jy1+su*1.5} ${j1+jw},${jy1}`} stroke={e} strokeWidth="0.8" fill="none" strokeLinecap="round" opacity={o}/>
        <path d={`M${j2-jw},${jy2} Q${j2},${jy2+su*1.5} ${j2+jw},${jy2}`} stroke={e} strokeWidth="0.8" fill="none" strokeLinecap="round" opacity={o}/>
      </g>;
    }

    if (auraId === 'joy') return <path d={`M${cx-w},${my} Q${cx},${my+su*3.5} ${cx+w},${my}`} stroke={e} strokeWidth="1.2" fill="none" strokeLinecap="round" opacity={o+0.1}/>;
    if (auraId === 'pain') return <line x1={cx-w*0.6} y1={my} x2={cx+w*0.6} y2={my} stroke={e} strokeWidth="1" strokeLinecap="round" opacity={o}/>;
    if (auraId === 'grief') return <line x1={cx-w*0.5} y1={my} x2={cx+w*0.5} y2={my+su*0.3} stroke={e} strokeWidth="1" strokeLinecap="round" opacity={o-0.05}/>;
    if (auraId === 'rest') return <line x1={cx-w*0.4} y1={my} x2={cx+w*0.4} y2={my} stroke={e} strokeWidth="0.8" strokeLinecap="round" opacity={o-0.05}/>;
    if (auraId === 'breathing') return <ellipse cx={cx} cy={my+su*0.5} rx={su*1.5} ry={su*1.8} fill="none" stroke={e} strokeWidth="1" opacity={o}/>;
    if (auraId === 'protection' || auraId === 'resistance') return <line x1={cx-w*0.7} y1={my} x2={cx+w*0.7} y2={my} stroke={e} strokeWidth="1" strokeLinecap="round" opacity={o+0.05}/>;
    if (['healing','community','spiritual','creative','careweave'].includes(auraId)) return <path d={`M${cx-w*0.8},${my} Q${cx},${my+su*2.5} ${cx+w*0.8},${my}`} stroke={e} strokeWidth="1.1" fill="none" strokeLinecap="round" opacity={o+0.05}/>;
    return <path d={`M${cx-w*0.7},${my} Q${cx},${my+su*1.5} ${cx+w*0.7},${my}`} stroke={e} strokeWidth="1" fill="none" strokeLinecap="round" opacity={o}/>;
  };

  return (
    <g transform={`scale(${breathe})`} style={{ transformOrigin: `${cx}px ${cy}px` }}>
      {/* Feet */}
      <ellipse cx={cx-su*8} cy={cy+su*26} rx={su*5.5} ry={su*3} fill={guide.body}/>
      <ellipse cx={cx+su*8} cy={cy+su*26} rx={su*5.5} ry={su*3} fill={guide.body}/>
      {/* Body */}
      {shapes[guide.shape]}
      {/* Arms */}
      <ellipse cx={cx-su*22} cy={cy+su*2} rx={su*5.5} ry={su*4} fill={guide.body} transform={`rotate(-18 ${cx-su*22} ${cy+su*2})`}/>
      <ellipse cx={cx+su*22} cy={cy+su*2} rx={su*5.5} ry={su*4} fill={guide.body} transform={`rotate(18 ${cx+su*22} ${cy+su*2})`}/>
      {/* Eyes */}
      {isJade ? <>
        <ellipse cx={cx-su*10} cy={cy-su*4+eyeDy} rx={su*2.8*eyeSz} ry={su*2.8*blink} fill={guide.eye}/>
        <ellipse cx={cx-su*4} cy={cy-su*4+eyeDy} rx={su*2.8*eyeSz} ry={su*2.8*blink} fill={guide.eye}/>
        <ellipse cx={cx+su*6} cy={cy-su*3+eyeDy} rx={su*2.5*eyeSz} ry={su*2.5*blink} fill={guide.eye}/>
        <ellipse cx={cx+su*11} cy={cy-su*3+eyeDy} rx={su*2.5*eyeSz} ry={su*2.5*blink} fill={guide.eye}/>
        {blink > 0.5 && <>
          <circle cx={cx-su*9} cy={cy-su*5+eyeDy} r={su*0.9} fill="white" opacity={eyeHi}/>
          <circle cx={cx-su*3} cy={cy-su*5+eyeDy} r={su*0.9} fill="white" opacity={eyeHi}/>
          <circle cx={cx+su*7} cy={cy-su*4+eyeDy} r={su*0.8} fill="white" opacity={eyeHi}/>
          <circle cx={cx+su*12} cy={cy-su*4+eyeDy} r={su*0.8} fill="white" opacity={eyeHi}/>
        </>}
      </> : <>
        <ellipse cx={cx-su*6} cy={cy-su*5+eyeDy} rx={su*3.5*eyeSz} ry={su*3.5*blink} fill={guide.eye}/>
        <ellipse cx={cx+su*6} cy={cy-su*5+eyeDy} rx={su*3.5*eyeSz} ry={su*3.5*blink} fill={guide.eye}/>
        {blink > 0.5 && <>
          <circle cx={cx-su*5} cy={cy-su*6+eyeDy} r={su*1.2} fill="white" opacity={eyeHi}/>
          <circle cx={cx+su*7} cy={cy-su*6+eyeDy} r={su*1.2} fill="white" opacity={eyeHi}/>
        </>}
      </>}
      {/* Cheeks */}
      <ellipse cx={cx-su*11} cy={cy-su*1} rx={su*4} ry={su*2.5} fill={guide.cheek} opacity="0.3"/>
      <ellipse cx={cx+su*11} cy={cy-su*1} rx={su*4} ry={su*2.5} fill={guide.cheek} opacity="0.3"/>
      {/* Mouth */}
      {renderMouth()}
    </g>
  );
}

// ── Globe Aura ──────────────────────────────────────────────────────────

function GlobeAura({
  auraId, phase, cx, cy, r,
}: {
  auraId: string; phase: number; cx: number; cy: number; r: number;
}) {
  const aura = AURAS.find(a => a.id === auraId);
  if (!aura || aura.id === 'none') return null;

  const p = phase;
  const ac = aura.color;
  const sin = Math.sin;
  const cos = Math.cos;

  switch (aura.id) {
    case 'breathing': {
      const br = r * 0.6 + sin(p * 0.8) * r * 0.15;
      return <g>
        <circle cx={cx} cy={cy} r={br} fill={ac} opacity={0.06 + sin(p * 0.8) * 0.04}/>
        <circle cx={cx} cy={cy} r={br} fill="none" stroke={ac} strokeWidth="2.5" opacity={0.2 + sin(p * 0.8) * 0.1}/>
        <circle cx={cx} cy={cy} r={br + r * 0.1} fill="none" stroke={ac} strokeWidth="1.5" opacity={0.12 + sin(p * 0.8) * 0.06}/>
      </g>;
    }
    case 'pain':
      return <g>
        <circle cx={cx} cy={cy} r={r * 0.75} fill={ac} opacity={sin(p * 0.5) * 0.05 + 0.08}/>
        <circle cx={cx} cy={cy} r={r * 0.6} fill={ac} opacity={sin(p * 0.5) * 0.06 + 0.1}/>
      </g>;
    case 'joy':
      return <g>
        <circle cx={cx} cy={cy} r={r * 0.7} fill={ac} opacity="0.06"/>
        {Array.from({ length: 10 }).map((_, i) => {
          const a = (i / 10) * Math.PI * 2 + p * 0.3;
          const d = r * (0.4 + sin(p + i) * 0.15);
          const rise = (p * 15 + i * 20) % 50 - 25;
          return <circle key={i} cx={cx + cos(a) * d * 0.7} cy={cy - rise * 1.5 + sin(a) * d * 0.3} r={2 + sin(p + i) * 1.2} fill={ac} opacity={0.35 + sin(p + i * 0.5) * 0.15}/>;
        })}
      </g>;
    case 'rest':
      return <g>
        <circle cx={cx} cy={cy + r * 0.05} r={r * 0.7} fill={ac} opacity="0.08"/>
        <ellipse cx={cx} cy={cy + r * 0.1} rx={r * 0.6} ry={r * 0.5} fill={ac} opacity="0.06"/>
        {Array.from({ length: 6 }).map((_, i) => {
          const px = cx + sin(p * 0.06 + i * 1.1) * r * 0.4;
          const fall = ((p * 3 + i * 12) % 50);
          const py = cy - r * 0.3 + fall * r * 0.016;
          return <circle key={i} cx={px} cy={py} r={1.5 + sin(p * 0.1 + i) * 0.5} fill={ac} opacity={0.18 + sin(p * 0.15 + i) * 0.06}/>;
        })}
        {[0, 1, 2].map(i => {
          const rise = ((p * 4 + i * 18) % 55);
          const baseX = cx + r * 0.1 + i * r * 0.12;
          const px = baseX + sin(p * 0.15 + i * 2) * r * 0.06;
          const py = cy - r * 0.05 - rise * r * 0.015;
          const sz = 6 + i * 3;
          const op = Math.max(0.3 - rise * 0.004, 0.02);
          return <text key={`z${i}`} x={px} y={py} textAnchor="middle" fontSize={sz} fill={ac} opacity={op} fontFamily="'Source Serif 4',Georgia,serif" fontWeight="600" fontStyle="italic">z</text>;
        })}
        {Array.from({ length: 3 }).map((_, i) => {
          const px = cx + sin(p * 0.04 + i * 1.8) * r * 0.3;
          const py = cy - r * 0.1 + cos(p * 0.03 + i * 1.4) * r * 0.18;
          return <circle key={`s${i}`} cx={px} cy={py} r={1} fill="white" opacity={0.12 + sin(p * 0.1 + i) * 0.06}/>;
        })}
      </g>;
    case 'healing':
      return <g>
        <ellipse cx={cx} cy={cy + r * 0.2} rx={r * 0.5} ry={r * 0.25} fill={ac} opacity="0.08"/>
        {Array.from({ length: 8 }).map((_, i) => {
          const sx = cx + (i - 4) * r * 0.1;
          const rise = ((p * 10 + i * 14) % 40);
          return <circle key={i} cx={sx + sin(p + i) * 5} cy={cy + r * 0.3 - rise * 2.5} r={Math.max(2.2 - rise * 0.04, 0.3)} fill={ac} opacity={Math.max(0.4 - rise * 0.008, 0)}/>;
        })}
      </g>;
    case 'protection':
      return <g>
        <circle cx={cx} cy={cy} r={r * 0.7} fill="none" stroke={ac} strokeWidth="3" opacity={0.25 + sin(p * 0.6) * 0.08}/>
        <circle cx={cx} cy={cy} r={r * 0.76} fill="none" stroke={ac} strokeWidth="2" opacity={0.15 + sin(p * 0.6) * 0.06}/>
        {[0, 90, 180, 270].map((a, i) => {
          const rad = (a + p * 5) * Math.PI / 180;
          return <circle key={i} cx={cx + cos(rad) * r * 0.72} cy={cy + sin(rad) * r * 0.72} r={2.5} fill={ac} opacity={0.35 + sin(p * 0.8 + i) * 0.12}/>;
        })}
      </g>;
    case 'resistance':
      return <g>
        <circle cx={cx} cy={cy} r={r * 0.65} fill={ac} opacity="0.05"/>
        {Array.from({ length: 5 }).map((_, i) => {
          const a = (i / 5) * Math.PI * 2 + 0.3;
          const gl = r * (0.3 + sin(p * 0.2 + i) * 0.1);
          const sx = cx + cos(a) * r * 0.25;
          const sy = cy + sin(a) * r * 0.25;
          const ex = cx + cos(a) * gl;
          const ey = cy + sin(a) * gl * 0.8;
          return <g key={i}>
            <path d={`M${sx},${sy} Q${(sx + ex) / 2 + sin(p * 0.3 + i) * 8},${(sy + ey) / 2} ${ex},${ey}`} stroke={ac} strokeWidth="2" fill="none" opacity={0.2 + sin(p * 0.3 + i) * 0.08} strokeLinecap="round"/>
            <circle cx={ex} cy={ey} r={2 + sin(p * 0.4 + i) * 1} fill={ac} opacity={0.3 + sin(p * 0.5 + i) * 0.1}/>
          </g>;
        })}
      </g>;
    case 'community':
      return <g>
        {Array.from({ length: 6 }).map((_, i) => {
          const spd = 0.2 + i * 0.05;
          const a = p * spd + (i / 6) * Math.PI * 2;
          const d = r * (0.5 + i * 0.03);
          return <g key={i}>
            <circle cx={cx + cos(a) * d} cy={cy + sin(a) * d * 0.5} r={3} fill={ac} opacity={0.4}/>
            <circle cx={cx + cos(a) * d} cy={cy + sin(a) * d * 0.5} r={1.5} fill="white" opacity={0.25}/>
          </g>;
        })}
      </g>;
    case 'grief':
      return <g>
        <ellipse cx={cx} cy={cy + r * 0.35} rx={r * 0.6} ry={r * 0.15} fill={ac} opacity="0.08"/>
        {Array.from({ length: 12 }).map((_, i) => {
          const sx = cx + (i - 6) * r * 0.09;
          const fall = ((p * 8 + i * 10) % 40);
          return <line key={i} x1={sx} y1={cy - r * 0.4 + fall * 2.5} x2={sx - 0.5} y2={cy - r * 0.4 + fall * 2.5 + 4} stroke={ac} strokeWidth="1.2" opacity={Math.max(0.2 - fall * 0.003, 0.03)} strokeLinecap="round"/>;
        })}
      </g>;
    case 'spiritual':
      return <g>
        <ellipse cx={cx} cy={cy - r * 0.1} rx={r * (0.2 + sin(p * 2) * 0.05)} ry={r * (0.3 + sin(p * 2.5) * 0.06)} fill={ac} opacity={0.06 + sin(p * 2) * 0.03}/>
        {[0, 1, 2].map(i => {
          const bx = cx + (i - 1) * r * 0.2;
          return <path key={i} d={`M${bx},${cy + r * 0.3} Q${bx + sin(p * 0.4 + i) * r * 0.15},${cy - r * 0.1} ${bx + sin(p * 0.3 + i) * r * 0.2},${cy - r * 0.4}`} stroke={ac} strokeWidth="2" fill="none" opacity={0.12 + sin(p * 0.5 + i) * 0.06} strokeLinecap="round"/>;
        })}
      </g>;
    case 'creative':
      return <g>
        <circle cx={cx} cy={cy} r={r * 0.6} fill={ac} opacity="0.04"/>
        {Array.from({ length: 8 }).map((_, i) => {
          const a = (i / 8) * Math.PI * 2 + p * 0.4;
          const d = r * (0.35 + sin(p + i) * 0.1);
          const colors = ['#c070b0', '#70b0c0', '#c8a050', '#c4788e', '#4a8c6f', '#8b6fb0', '#e8c040', '#60b898'];
          return <circle key={i} cx={cx + cos(a) * d} cy={cy + sin(a) * d * 0.6} r={2.5 + sin(p * 0.5 + i) * 1} fill={colors[i]} opacity={0.3 + sin(p * 0.4 + i) * 0.1}/>;
        })}
      </g>;
    case 'careweave':
      return <g>
        {Array.from({ length: 8 }).map((_, i) => {
          const a = (i / 8) * Math.PI * 2;
          const wb = sin(p * 0.4 + i * 0.8) * r * 0.05;
          const r1 = r * 0.45;
          const r2 = r * 0.6 + wb;
          const x1 = cx + cos(a) * r1;
          const y1 = cy + sin(a) * r1 * 0.7;
          const na = ((i + 3) / 8) * Math.PI * 2;
          const x2 = cx + cos(na) * r2;
          const y2 = cy + sin(na) * r2 * 0.7;
          return <path key={i} d={`M${x1},${y1} Q${cx + wb},${cy} ${x2},${y2}`} stroke={ac} strokeWidth="1.5" fill="none" opacity={0.18 + sin(p * 0.5 + i) * 0.08}/>;
        })}
        <circle cx={cx} cy={cy} r={r * 0.3} fill={ac} opacity="0.04"/>
      </g>;
    default:
      return null;
  }
}

// ── Comfort Item Graphics ───────────────────────────────────────────────

function ComfortItemGraphic({ id, px, py, u }: { id: string; px: number; py: number; u: number }) {
  const graphics: Record<string, React.ReactElement> = {
    water: <g transform={`translate(${px},${py})`}>
      <rect x={-u*4} y={-u*8} width={u*8} height={u*14} rx={u*3} fill="#1a3d5c"/>
      <rect x={-u*3} y={-u*7} width={u*6} height={u*12} rx={u*2.5} fill="#2b5c8a"/>
      <ellipse cx={0} cy={u*1} rx={u*2.5} ry={u*4} fill="#3a7ab0" opacity="0.5"/>
      <rect x={-u*2.5} y={-u*11} width={u*5} height={u*4} rx={u*2} fill="#1a3d5c"/>
      <ellipse cx={u*1.5} cy={-u*2} rx={u*0.8} ry={u*3} fill="white" opacity="0.07"/>
    </g>,
    phone: <g transform={`translate(${px},${py})`}>
      <rect x={-u*3.5} y={-u*6.5} width={u*7} height={u*12} rx={u*1.8} fill="#2a2a3a" stroke="#4a4a5a" strokeWidth={u*0.5}/>
      <rect x={-u*2.8} y={-u*5} width={u*5.6} height={u*9} rx={u*0.6} fill="#1a1a28"/>
      <rect x={-u*2.8} y={-u*5} width={u*5.6} height={u*9} rx={u*0.6} fill="#d4a843" opacity="0.06"/>
    </g>,
    candle: <g transform={`translate(${px},${py})`}>
      <rect x={-u*2.5} y={-u*4} width={u*5} height={u*12} rx={u*2} fill="#d8d0c0"/>
      <rect x={-u*2} y={-u*3} width={u*4} height={u*10} rx={u*1.5} fill="#e8e0d0" opacity="0.5"/>
      <line x1={0} y1={-u*4} x2={0} y2={-u*6.5} stroke="#c8b080" strokeWidth={u*0.5}/>
      <ellipse cx={0} cy={-u*7.5} rx={u*2} ry={u*2.8} fill="#e8c040" opacity="0.5"/>
      <ellipse cx={0} cy={-u*8} rx={u*1} ry={u*1.6} fill="#f0e080" opacity="0.4"/>
    </g>,
    plushie: <g transform={`translate(${px},${py})`}>
      <circle cx={0} cy={u*1.5} r={u*4} fill="#8a6a50"/>
      <circle cx={0} cy={u*2} r={u*3} fill="#a08060" opacity="0.4"/>
      <circle cx={0} cy={-u*3} r={u*3.2} fill="#9a7a5a"/>
      <circle cx={-u*2.5} cy={-u*5} r={u*1.4} fill="#8a6a50"/>
      <circle cx={u*2.5} cy={-u*5} r={u*1.4} fill="#8a6a50"/>
      <circle cx={-u*1.2} cy={-u*3.5} r={u*0.6} fill="#3a2a18"/>
      <circle cx={u*1.2} cy={-u*3.5} r={u*0.6} fill="#3a2a18"/>
      <ellipse cx={0} cy={-u*2.2} rx={u*0.7} ry={u*0.5} fill="#4a3020"/>
      <path d={`M${-u*0.8},${-u*1.5} Q${0},${-u*0.8} ${u*0.8},${-u*1.5}`} stroke="#4a3020" strokeWidth={u*0.3} fill="none" strokeLinecap="round" opacity="0.4"/>
    </g>,
    journal: <g transform={`translate(${px},${py})`}>
      <rect x={-u*4} y={-u*5.5} width={u*8} height={u*11} rx={u*1.2} fill="#2a3a5a"/>
      <rect x={-u*3.5} y={-u*5} width={u*7} height={u*10} rx={u*1} fill="#1a2a4a"/>
      <line x1={-u*4} y1={-u*5.5} x2={-u*4} y2={u*5.5} stroke="#4a6a9a" strokeWidth={u*1}/>
      {([-2.5, 0, 2.5] as number[]).map((y, j) => <line key={j} x1={-u*2} y1={u*y} x2={u*3} y2={u*y} stroke="#4a6a9a" strokeWidth={u*0.3} opacity="0.3"/>)}
    </g>,
    fidget: <g transform={`translate(${px},${py})`}>
      {([0, 120, 240] as number[]).map((angle, j) => {
        const rad = (angle - 90) * Math.PI / 180;
        const lx = Math.cos(rad) * u * 3.5;
        const ly = Math.sin(rad) * u * 3.5;
        const cols = ['#c4788e', '#d4a843', '#4a8c6f'];
        return <g key={j}>
          <line x1={0} y1={0} x2={lx} y2={ly} stroke="#3a3a4a" strokeWidth={u*1.8} strokeLinecap="round"/>
          <circle cx={lx} cy={ly} r={u*2.5} fill={cols[j]}/>
          <circle cx={lx} cy={ly} r={u*1.5} fill={cols[j]} opacity="0.5"/>
        </g>;
      })}
      <circle cx={0} cy={0} r={u*2} fill="#2a2a3a"/>
      <circle cx={0} cy={0} r={u*1.2} fill="#3a3a4a"/>
    </g>,
    controller: <g transform={`translate(${px},${py})`}>
      <rect x={-u*6.5} y={-u*3.5} width={u*13} height={u*7} rx={u*3} fill="#2a2a3a" stroke="#4a4a5a" strokeWidth={u*0.5}/>
      <rect x={-u*4} y={-u*1.2} width={u*1.2} height={u*3} rx={u*0.4} fill="#4a4a5a"/>
      <rect x={-u*5} y={u*0.5} width={u*3} height={u*1.2} rx={u*0.4} fill="#4a4a5a"/>
      <circle cx={u*3} cy={-u*0.5} r={u*0.9} fill="#c4788e"/>
      <circle cx={u*4.5} cy={u*1} r={u*0.9} fill="#4a8c6f"/>
      <circle cx={u*3} cy={u*2.5} r={u*0.9} fill="#d4a843"/>
      <circle cx={u*1.5} cy={u*1} r={u*0.9} fill="#2b5c8a"/>
    </g>,
    matcha: <g transform={`translate(${px},${py})`}>
      <rect x={-u*4} y={-u*4} width={u*8} height={u*10} rx={u*3} fill="#d8d0c0"/>
      <rect x={-u*3.5} y={-u*3.5} width={u*7} height={u*9} rx={u*2.5} fill="#e8e0d0" opacity="0.5"/>
      <ellipse cx={0} cy={-u*1} rx={u*3} ry={u*2.5} fill="#5a8a4a" opacity="0.5"/>
      <ellipse cx={0} cy={-u*1.5} rx={u*2} ry={u*1.5} fill="#7aaa6a" opacity="0.3"/>
      <path d={`M${-u*1},${-u*2} Q${0},${-u*3} ${u*1},${-u*2}`} stroke="white" strokeWidth={u*0.3} fill="none" opacity="0.3"/>
      <path d={`M${u*4},${-u*1} Q${u*6.5},${0} ${u*4},${u*3}`} stroke="#d8d0c0" strokeWidth={u*1.5} fill="none" strokeLinecap="round"/>
    </g>,
    boba: <g transform={`translate(${px},${py})`}>
      <rect x={-u*4} y={-u*5} width={u*8} height={u*13} rx={u*3} fill="#c8a878"/>
      <rect x={-u*3} y={-u*4} width={u*6} height={u*11} rx={u*2.5} fill="#dcc098" opacity="0.6"/>
      <ellipse cx={0} cy={u*1} rx={u*2.5} ry={u*3.5} fill="#a87840" opacity="0.35"/>
      {([[-1,3],[1,5],[0,1.5],[1.5,6],[-1.5,5]] as [number,number][]).map(([dx,dy],j) =>
        <circle key={j} cx={u*dx} cy={u*dy} r={u*0.9} fill="#3a2818" opacity="0.6"/>
      )}
      <ellipse cx={0} cy={-u*5} rx={u*4} ry={u*1.8} fill="#b89868"/>
      <rect x={-u*0.8} y={-u*13} width={u*1.6} height={u*9} rx={u*0.8} fill="#c8a878"/>
    </g>,
    sketchbook: <g transform={`translate(${px},${py})`}>
      <rect x={-u*4.5} y={-u*5} width={u*9} height={u*11} rx={u*1} fill="#d8c8a8" stroke="#b8a888" strokeWidth={u*0.4}/>
      <rect x={-u*4} y={-u*4.5} width={u*8} height={u*10} rx={u*0.8} fill="#e8dcc4"/>
      <circle cx={-u*1} cy={-u*1} r={u*1.5} fill="none" stroke="#c070b0" strokeWidth={u*0.5}/>
      <path d={`M${u*1},${u*2} Q${u*2.5},${u*1} ${u*3},${u*3}`} stroke="#70b0c0" strokeWidth={u*0.5} fill="none"/>
    </g>,
    camera: <g transform={`translate(${px},${py})`}>
      <rect x={-u*5} y={-u*3} width={u*10} height={u*7} rx={u*2} fill="#2a2a3a" stroke="#4a4a5a" strokeWidth={u*0.4}/>
      <circle cx={0} cy={u*0.5} r={u*2.5} fill="#1a1a28" stroke="#4a4a5a" strokeWidth={u*0.4}/>
      <circle cx={0} cy={u*0.5} r={u*1.2} fill="#3a5a8a" opacity="0.5"/>
      <rect x={u*2} y={-u*4.5} width={u*2} height={u*2} rx={u*0.5} fill="#3a3a4a"/>
    </g>,
    book: <g transform={`translate(${px},${py})`}>
      <rect x={-u*3.5} y={-u*5.5} width={u*7} height={u*11} rx={u*1} fill="#5a3a6a"/>
      <rect x={-u*3} y={-u*5} width={u*6} height={u*10} rx={u*0.8} fill="#7a5a8a" opacity="0.5"/>
      <line x1={-u*3.5} y1={-u*5.5} x2={-u*3.5} y2={u*5.5} stroke="#8b6fb0" strokeWidth={u*0.8}/>
      <rect x={-u*1.5} y={-u*3} width={u*3} height={u*3} rx={u*0.5} fill="#c070b0" opacity="0.3"/>
    </g>,
    cat: <g transform={`translate(${px},${py})`}>
      <ellipse cx={0} cy={u*2} rx={u*5.5} ry={u*3.5} fill="#4a4a5a"/>
      <ellipse cx={0} cy={u*1.5} rx={u*4.5} ry={u*2.8} fill="#5a5a6a" opacity="0.4"/>
      <circle cx={-u*3} cy={-u*2} r={u*3.5} fill="#4a4a5a"/>
      <circle cx={-u*3} cy={-u*1.5} r={u*2.5} fill="#5a5a6a" opacity="0.3"/>
      <polygon points={`${-u*5},${-u*3.5} ${-u*4.5},${-u*6} ${-u*3},${-u*4}`} fill="#4a4a5a"/>
      <polygon points={`${-u*1},${-u*3.5} ${-u*1.5},${-u*6} ${-u*3},${-u*4}`} fill="#4a4a5a"/>
      <polygon points={`${-u*4.8},${-u*3.8} ${-u*4.5},${-u*5.5} ${-u*3.3},${-u*4.2}`} fill="#6a5a6a" opacity="0.4"/>
      <polygon points={`${-u*1.2},${-u*3.8} ${-u*1.5},${-u*5.5} ${-u*2.7},${-u*4.2}`} fill="#6a5a6a" opacity="0.4"/>
      <line x1={-u*4} y1={-u*2} x2={-u*3.2} y2={-u*2} stroke="#c0c0d8" strokeWidth={u*0.6} strokeLinecap="round"/>
      <line x1={-u*2.8} y1={-u*2} x2={-u*2} y2={-u*2} stroke="#c0c0d8" strokeWidth={u*0.6} strokeLinecap="round"/>
      <circle cx={-u*3} cy={-u*1} r={u*0.4} fill="#8a7a8a"/>
      <path d={`M${u*5},${u*1} Q${u*7},${-u*1} ${u*6},${-u*3} Q${u*5},${-u*4.5} ${u*3.5},${-u*4}`} stroke="#4a4a5a" strokeWidth={u*1.5} fill="none" strokeLinecap="round"/>
    </g>,
    mic: <g transform={`translate(${px},${py})`}>
      <circle cx={0} cy={-u*4} r={u*3} fill="#3a3a4a"/>
      <circle cx={0} cy={-u*4} r={u*2.2} fill="#4a4a5a" opacity="0.5"/>
      {([-1.5, 0, 1.5] as number[]).map((dy, j) => <line key={j} x1={-u*1.5} y1={-u*4+u*dy} x2={u*1.5} y2={-u*4+u*dy} stroke="#5a5a6a" strokeWidth={u*0.3} opacity="0.4"/>)}
      <rect x={-u*0.8} y={-u*1} width={u*1.6} height={u*8} rx={u*0.8} fill="#3a3a4a"/>
      <rect x={-u*3} y={u*6.5} width={u*6} height={u*1.2} rx={u*0.6} fill="#3a3a4a"/>
    </g>,
    palette: <g transform={`translate(${px},${py})`}>
      <ellipse cx={0} cy={0} rx={u*5.5} ry={u*4} fill="#8a6a40"/>
      <ellipse cx={0} cy={0} rx={u*4.5} ry={u*3} fill="#a88050" opacity="0.5"/>
      <circle cx={-u*2} cy={-u*1} r={u*1} fill="#c4788e"/>
      <circle cx={u*0.5} cy={-u*1.5} r={u*1} fill="#d4a843"/>
      <circle cx={u*2.5} cy={-u*0.5} r={u*1} fill="#4a8c6f"/>
      <circle cx={u*1} cy={u*1.2} r={u*1} fill="#2b5c8a"/>
      <circle cx={-u*1.5} cy={u*1.5} r={u*1} fill="#c070b0"/>
      <ellipse cx={u*3} cy={u*1.5} rx={u*1.2} ry={u*1} fill="#a88050"/>
    </g>,
    knitting: <g transform={`translate(${px},${py})`}>
      <circle cx={0} cy={u*1} r={u*4} fill="#c070b0"/>
      <circle cx={0} cy={u*1} r={u*3} fill="#d090c0" opacity="0.4"/>
      <path d={`M${-u*2},${-u*1} Q${0},${-u*3} ${u*2},${-u*1}`} stroke="#d090c0" strokeWidth={u*0.5} fill="none" opacity="0.5"/>
      <line x1={-u*1.5} y1={-u*4} x2={u*0.5} y2={u*3} stroke="#8a6a40" strokeWidth={u*0.5} strokeLinecap="round"/>
      <line x1={u*1.5} y1={-u*4} x2={u*2.5} y2={u*2} stroke="#8a6a40" strokeWidth={u*0.5} strokeLinecap="round"/>
    </g>,
    sign: <g transform={`translate(${px},${py})`}>
      <rect x={-u*0.6} y={-u*2} width={u*1.2} height={u*10} rx={u*0.4} fill="#8a6a40"/>
      <rect x={-u*5} y={-u*8} width={u*10} height={u*7} rx={u*1} fill="#e8e0d0"/>
      <rect x={-u*4.5} y={-u*7.5} width={u*9} height={u*6} rx={u*0.8} fill="#f0e8e0" opacity="0.5"/>
    </g>,
    icedcoffee: <g transform={`translate(${px},${py})`}>
      <rect x={-u*3.5} y={-u*5} width={u*7} height={u*12} rx={u*2.5} fill="#e8e0d8" opacity="0.7"/>
      <rect x={-u*3} y={-u*4} width={u*6} height={u*10} rx={u*2} fill="#6a4a2a" opacity="0.4"/>
      <ellipse cx={0} cy={-u*1} rx={u*2} ry={u*2} fill="#e8e0d8" opacity="0.3"/>
      <ellipse cx={u*1} cy={u*1} rx={u*1.5} ry={u*1.5} fill="#e8e0d8" opacity="0.25"/>
      <rect x={-u*3} y={-u*5.5} width={u*6} height={u*2} rx={u*1} fill="#e8e0d8" opacity="0.5"/>
      <rect x={-u*0.6} y={-u*12} width={u*1.2} height={u*8} rx={u*0.6} fill="#4a8c6f"/>
    </g>,
    basketball: <g transform={`translate(${px},${py})`}>
      <circle cx={0} cy={0} r={u*6} fill="#c86828"/>
      <circle cx={0} cy={0} r={u*5} fill="#d88038" opacity="0.4"/>
      <path d={`M${-u*6},${0} Q${-u*3},${-u*3} ${0},${-u*6}`} stroke="#a05018" strokeWidth={u*0.6} fill="none" opacity="0.5"/>
      <path d={`M${u*6},${0} Q${u*3},${u*3} ${0},${u*6}`} stroke="#a05018" strokeWidth={u*0.6} fill="none" opacity="0.5"/>
      <line x1={-u*6} y1={0} x2={u*6} y2={0} stroke="#a05018" strokeWidth={u*0.5} opacity="0.4"/>
      <line x1={0} y1={-u*6} x2={0} y2={u*6} stroke="#a05018" strokeWidth={u*0.5} opacity="0.4"/>
      <circle cx={u*2} cy={-u*2} r={u*1.5} fill="white" opacity="0.08"/>
    </g>,
    soccerball: <g transform={`translate(${px},${py})`}>
      <circle cx={0} cy={0} r={u*6} fill="#e8e0d8"/>
      <circle cx={0} cy={0} r={u*5} fill="#f0ece8" opacity="0.4"/>
      {([[0,-u*2],[u*3.2,u*2.2],[-u*3.2,u*2.2],[u*2.2,-u*4],[-u*2.2,-u*4]] as [number,number][]).map(([cx2, cy2], j) => {
        const pr = j === 0 ? u * 2 : u * 1.6;
        const pts = [0,1,2,3,4].map(k => {
          const a = (k / 5) * Math.PI * 2 - Math.PI / 2;
          return `${cx2 + Math.cos(a) * pr},${cy2 + Math.sin(a) * pr}`;
        }).join(' ');
        return <polygon key={j} points={pts} fill="#2a2a3a" opacity={j === 0 ? 0.6 : 0.45}/>;
      })}
      <circle cx={u*1.5} cy={-u*1} r={u*1} fill="white" opacity="0.08"/>
    </g>,
  };

  return graphics[id] ? <g>{graphics[id]}</g> : null;
}

// ── Main CrystalGlobe Component ─────────────────────────────────────────

export default function CrystalGlobe({
  guide, auraId, equippedItems = [], size = 340, reducedMotion = false,
}: CrystalGlobeProps) {
  const phase = useAnimPhase(reducedMotion);
  const uid = useId();
  const dark = useDarkMode();
  const bg = themeBg(guide, dark);

  const w = size;
  const h = size * 1.15;
  const globeR = w * 0.38;
  const globeCx = w / 2;
  const globeCy = h * 0.38;
  const crystalCy = globeCy + globeR * 0.15;
  const su = globeR * 0.03 * 0.42;
  const breathe = reducedMotion ? 1 : 1 + Math.sin(phase) * 0.01;

  const gradInner = `globeInner-${uid}`;
  const gradGlobe = `globeGrad-${uid}`;
  const clipGlobe = `globeClip-${uid}`;

  // Theme-aware colours
  const pedestal = dark
    ? { base: '#1a1828', mid: '#22203a', top: '#2a2840', stroke: 'rgba(139,111,176,0.15)' }
    : { base: '#c8c0d8', mid: '#d8d0e8', top: '#e4daf0', stroke: 'rgba(139,111,176,0.25)' };
  const groundFill = dark ? '#2a2838' : '#d4cce4';
  const glassStroke1 = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const glassStroke2 = dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)';
  const glassShine = dark ? 'white' : 'white';
  const glassShineOp = dark ? '0.06' : '0.05';
  const glassShine2Op = dark ? '0.02' : '0.02';
  const highlightStroke = dark ? 'white' : 'rgba(255,255,255,0.9)';
  const highlightOp = dark ? '0.08' : '0.10';
  const shadowOp = dark ? '0.15' : '0.08';

  // Item placement
  const handL: [number, number] = [globeCx - globeR * 0.28, crystalCy + globeR * 0.02];
  const handR: [number, number] = [globeCx + globeR * 0.28, crystalCy + globeR * 0.02];
  const groundPositions: [number, number][] = [
    [globeCx - globeR * 0.42, globeCy + globeR * 0.34],
    [globeCx + globeR * 0.42, globeCy + globeR * 0.34],
    [globeCx - globeR * 0.22, globeCy + globeR * 0.42],
    [globeCx + globeR * 0.22, globeCy + globeR * 0.42],
    [globeCx - globeR * 0.5, globeCy + globeR * 0.24],
  ];

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ overflow: 'visible' }} role="img" aria-label={`${guide.name} crystal globe`}>
      <defs>
        <radialGradient id={gradGlobe} cx="40%" cy="35%" r="50%">
          <stop offset="0%" stopColor={glassShine} stopOpacity={glassShineOp}/>
          <stop offset="60%" stopColor={glassShine} stopOpacity={glassShine2Op}/>
          <stop offset="100%" stopColor={glassShine} stopOpacity="0"/>
        </radialGradient>
        <clipPath id={clipGlobe}>
          <circle cx={globeCx} cy={globeCy} r={globeR}/>
        </clipPath>
        <radialGradient id={gradInner} cx="50%" cy="70%" r="55%">
          <stop offset="0%" stopColor={guide.accent} stopOpacity="0.35"/>
          <stop offset="35%" stopColor={guide.belly} stopOpacity="0.15"/>
          <stop offset="70%" stopColor={bg[1]} stopOpacity="0.85"/>
          <stop offset="100%" stopColor={bg[0]} stopOpacity="1"/>
        </radialGradient>
      </defs>

      {/* Globe shadow */}
      <ellipse cx={globeCx} cy={globeCy + globeR + 20} rx={globeR * 0.7} ry={12} fill="#000" opacity={shadowOp}/>

      {/* Pedestal */}
      <ellipse cx={globeCx} cy={globeCy + globeR + 8} rx={globeR * 0.55} ry={18} fill={pedestal.base}/>
      <ellipse cx={globeCx} cy={globeCy + globeR + 4} rx={globeR * 0.52} ry={15} fill={pedestal.mid}/>
      <ellipse cx={globeCx} cy={globeCy + globeR} rx={globeR * 0.48} ry={12} fill={pedestal.top} stroke={pedestal.stroke} strokeWidth="1"/>

      {/* Globe interior */}
      <circle cx={globeCx} cy={globeCy} r={globeR} fill={`url(#${gradInner})`}/>

      <g clipPath={`url(#${clipGlobe})`}>
        {/* Upper shading */}
        <ellipse cx={globeCx} cy={globeCy - globeR * 0.2} rx={globeR} ry={globeR * 0.6} fill={bg[0]} opacity="0.5"/>
        {/* Warm ambient light */}
        <ellipse cx={globeCx} cy={globeCy + globeR * 0.2} rx={globeR * 0.95} ry={globeR * 0.6} fill={guide.accent} opacity="0.25"/>
        {/* Ground */}
        <ellipse cx={globeCx} cy={globeCy + globeR * 0.62} rx={globeR * 0.98} ry={globeR * 0.35} fill={groundFill} opacity="0.7"/>
        <ellipse cx={globeCx} cy={globeCy + globeR * 0.58} rx={globeR * 0.85} ry={globeR * 0.28} fill={guide.accent} opacity="0.3"/>
        <ellipse cx={globeCx} cy={globeCy + globeR * 0.52} rx={globeR * 0.7} ry={globeR * 0.2} fill={guide.accent} opacity="0.15"/>
        {/* Foot shadow */}
        <ellipse cx={globeCx} cy={crystalCy + globeR * 0.22} rx={globeR * 0.18} ry={globeR * 0.04} fill="rgba(0,0,0,0.18)"/>

        {/* Aura */}
        <GlobeAura auraId={auraId} phase={phase} cx={globeCx} cy={globeCy} r={globeR}/>

        {/* Crystal companion */}
        <CrystalBody guide={guide} auraId={auraId} phase={phase} cx={globeCx} cy={crystalCy} su={su} breathe={breathe}/>

        {/* Comfort items */}
        {equippedItems.map((id, i) => {
          const u = globeR * 0.026;
          let px: number, py: number;
          if (i === 0) { [px, py] = handL; }
          else if (i === 1) { [px, py] = handR; }
          else { [px, py] = groundPositions[(i - 2) % groundPositions.length]; }
          return <ComfortItemGraphic key={id} id={id} px={px} py={py} u={u}/>;
        })}
      </g>

      {/* Globe glass */}
      <circle cx={globeCx} cy={globeCy} r={globeR} fill="none" stroke={glassStroke1} strokeWidth="2"/>
      <circle cx={globeCx} cy={globeCy} r={globeR - 1} fill="none" stroke={glassStroke2} strokeWidth="1"/>
      <circle cx={globeCx} cy={globeCy} r={globeR} fill={`url(#${gradGlobe})`}/>
      {/* Top highlight arc */}
      <path d={`M${globeCx - globeR*0.5},${globeCy - globeR*0.75} Q${globeCx},${globeCy - globeR*0.9} ${globeCx + globeR*0.3},${globeCy - globeR*0.8}`} fill="none" stroke={highlightStroke} strokeWidth="1.5" opacity={highlightOp} strokeLinecap="round"/>
    </svg>
  );
}

// ── Compact Crystal Body (for small avatar bubbles) ─────────────────────

export function CrystalBodyOnly({ guide, auraId = 'none', size = 60, freeform = false, faceCloseUp = false }: CrystalBodyOnlyProps) {
  const phase = useAnimPhase(size <= 40);
  const dark = useDarkMode();
  const bg = themeBg(guide, dark);
  // Unique ID per instance to avoid clip-path collisions when same guide renders multiple times
  const uid = useId();
  // For face close-up, render at a larger internal size then crop via viewBox
  // Boost multiplier at tiny sizes so features remain visible
  const closeUpScale = size < 40 ? 3.2 : 2.4;
  const renderSize = faceCloseUp ? size * closeUpScale : size;
  const cx = renderSize / 2;
  const cy = renderSize / 2;
  const su = renderSize * 0.38 * 0.03 * 0.42;
  const breathe = 1 + Math.sin(phase) * 0.005;

  if (freeform) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: 'visible' }} role="img" aria-label={guide.name}>
        <CrystalBody guide={guide} auraId={auraId} phase={phase} cx={cx} cy={cy} su={su} breathe={breathe}/>
      </svg>
    );
  }

  if (faceCloseUp) {
    // Crop to the head/face region: centre on the face (slightly above body centre)
    // and show a tight circle around it
    const faceCy = cy - su * 8;
    const cropR = su * 18;
    const vbX = cx - cropR;
    const vbY = faceCy - cropR;
    const vbSize = cropR * 2;
    const clipId = `face-clip-${uid}`;
    // Lighten the background for dark guides in dark mode to prevent body disappearing
    const bodyLum = parseInt(guide.body.slice(1, 3), 16);
    const bgFill = dark && bodyLum < 0x30 ? guide.belly : bg[1];
    return (
      <svg width={size} height={size} viewBox={`${vbX} ${vbY} ${vbSize} ${vbSize}`} role="img" aria-label={guide.name}>
        <defs>
          <clipPath id={clipId}>
            <circle cx={cx} cy={faceCy} r={cropR}/>
          </clipPath>
        </defs>
        <circle cx={cx} cy={faceCy} r={cropR} fill={bgFill}/>
        {/* Subtle rim for dark-on-dark contrast */}
        <circle cx={cx} cy={faceCy} r={cropR - 0.5} fill="none" stroke={guide.accent} strokeWidth="1" opacity="0.25"/>
        <g clipPath={`url(#${clipId})`}>
          <CrystalBody guide={guide} auraId={auraId} phase={phase} cx={cx} cy={cy} su={su} breathe={breathe}/>
        </g>
      </svg>
    );
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: 'visible' }} role="img" aria-label={guide.name}>
      <defs>
        <clipPath id={`body-clip-${guide.id}`}>
          <circle cx={cx} cy={cy} r={size * 0.46}/>
        </clipPath>
      </defs>
      <circle cx={cx} cy={cy} r={size * 0.48} fill={bg[1]}/>
      <g clipPath={`url(#body-clip-${guide.id})`}>
        <CrystalBody guide={guide} auraId={auraId} phase={phase} cx={cx} cy={cy} su={su} breathe={breathe}/>
      </g>
    </svg>
  );
}
